import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, X, ArrowRight, Music2, Award, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import { getFileUrl } from "@/components/FileUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_TABS = [
  { key: "all", label: "All", icon: Camera },
  { key: "artist_photo", label: "Artist", icon: User },
  { key: "label", label: "Label", icon: Award },
  { key: "general", label: "General", icon: Music2 },
];

// ── Clean title logic ──
function cleanTitle(img) {
  const raw = (img.title || img.original_filename || "").trim();
  const type = img.image_type || "general";

  // Already a clean human-written title (no file extension, no UUID pattern)
  if (raw && !/\.[a-z]{3,4}$/i.test(raw) && !/^[A-F0-9-]{20,}/i.test(raw)) {
    return raw;
  }

  // Strip URL encoding
  let name = decodeURIComponent(raw);
  // Remove file extension
  name = name.replace(/\.[a-zA-Z0-9]{2,5}$/, "");
  // Replace underscores/hyphens with spaces
  name = name.replace(/[_-]/g, " ").trim();

  // If it's a UUID-style or camera filename like IMG_1900, generate a nice title
  const isRawFilename = /^(IMG|DSC|DCIM|Photo|Screenshot|[A-F0-9]{6,})/i.test(name);

  if (isRawFilename || !name) {
    if (type === "artist_photo") return "Luxifer 6";
    if (type === "label") return "3rd Degree Ent";
    return "Luxifer 6";
  }

  // Detect cover art
  if (/cover/i.test(name)) {
    // "Fed Up Cover Art (3D Mockup)" → "Fed Up – Cover Art"
    name = name.replace(/\s*\(.*?\)\s*/g, "").trim();
    if (!/–|—|-/.test(name)) {
      name = name.replace(/\s*(cover\s*art?)\s*/i, " – Cover");
    }
    return name.trim();
  }

  // Detect logo references
  if (type === "label" || /logo|brand|ent/i.test(name)) {
    return "3rd Degree Ent";
  }

  // Clean up "Luxifer 61xxx" style
  name = name.replace(/\b1xxx\b/gi, "6").replace(/\b61xxx\b/gi, "6");

  return name || "Luxifer 6";
}

// ── Determine where clicking should navigate ──
function getDestination(img) {
  const type = img.image_type || "general";
  const title = cleanTitle(img).toLowerCase();
  if (type === "artist_photo") return { label: "View Music", path: "/music" };
  if (type === "label") return { label: "View Label", path: "/label" };
  if (/cover|album|ep|single|track/i.test(title)) return { label: "View Music", path: "/music" };
  return { label: "View Music", path: "/music" };
}

// ── Category subtitle for lightbox ──
function getCategoryLabel(type) {
  if (type === "artist_photo") return "Artist";
  if (type === "label") return "Label / Branding";
  if (type === "artwork") return "Artwork";
  return "Gallery";
}

export default function Gallery() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/images?_t=${Date.now()}`).then(r => { setImages(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? images : images.filter(i => i.image_type === filter);
  const existingTypes = new Set(images.map(i => i.image_type));
  const tabs = FILTER_TABS.filter(t => t.key === "all" || existingTypes.has(t.key));

  const openLightbox = (img) => setLightbox(img);
  const closeLightbox = () => setLightbox(null);

  return (
    <div className="pt-16">
      {/* ── Header ── */}
      <div className="relative py-24 sm:py-32 px-6 bg-gradient-to-b from-[#D4AF37]/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Visuals</p>
          <h1 data-testid="gallery-page-title" className="font-heading text-5xl sm:text-6xl lg:text-7xl">GALLERY</h1>
          <p className="text-white/50 mt-4 max-w-lg">Behind the scenes, artwork, and moments captured.</p>
        </div>
      </div>

      <div className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* ── Filter Tabs ── */}
          {tabs.length > 1 && (
            <div data-testid="gallery-filter-bar" className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 scrollbar-hide">
              {tabs.map(t => {
                const Icon = t.icon;
                const count = t.key === "all" ? images.length : images.filter(i => i.image_type === t.key).length;
                return (
                  <button
                    key={t.key}
                    data-testid={`gallery-filter-${t.key}`}
                    onClick={() => setFilter(t.key)}
                    className={`flex items-center gap-2 text-xs uppercase tracking-wider font-semibold px-5 py-2.5 border whitespace-nowrap transition-all duration-300 ${
                      filter === t.key
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                    <span className={`text-[9px] ml-1 rounded-full w-5 h-5 flex items-center justify-center ${
                      filter === t.key ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/5 text-white/30"
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Grid ── */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-[#111111] border border-white/5 aspect-square animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-white/30">
              <Camera className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">{images.length === 0 ? "Gallery coming soon." : "No images in this category."}</p>
              {filter !== "all" && (
                <button onClick={() => setFilter("all")} className="text-sm mt-2 text-[#D4AF37] hover:text-white transition-colors">
                  View all
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {filtered.map((img, index) => {
                const title = cleanTitle(img);
                const cat = getCategoryLabel(img.image_type);
                return (
                  <AnimatedSection key={img.id} testId={`gallery-image-${img.id}`} delay={index * 60}>
                    <div
                      data-testid={`gallery-card-${img.id}`}
                      className="relative aspect-square overflow-hidden bg-[#0A0A0A] border border-white/[0.06] hover:border-[#D4AF37]/30 cursor-pointer group transition-all duration-500"
                      onClick={() => openLightbox(img)}
                    >
                      {/* Image */}
                      <img
                        src={getFileUrl(img.storage_path)}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                        loading="lazy"
                      />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />

                      {/* Category pill — always visible, top-left */}
                      <div className="absolute top-2.5 left-2.5 z-10">
                        <span className="text-[9px] uppercase tracking-wider bg-black/60 backdrop-blur-sm text-white/60 px-2 py-1 rounded-sm font-semibold">
                          {cat}
                        </span>
                      </div>

                      {/* Title + action on hover */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 z-10">
                        <p data-testid={`gallery-title-${img.id}`} className="text-white text-sm sm:text-base font-semibold leading-tight truncate">
                          {title}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-medium">
                            View
                          </span>
                          <ArrowRight className="w-3 h-3 text-[#D4AF37]" />
                        </div>
                      </div>

                      {/* Gold corner accents on hover */}
                      <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-all duration-500" />
                      <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-all duration-500" />
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Fullscreen Lightbox ── */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/[0.97] flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
        >
          {/* Close button */}
          <button
            data-testid="gallery-lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 w-10 h-10 flex items-center justify-center border border-white/10 hover:border-white/30 text-white/50 hover:text-white transition-all duration-300"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative w-full max-w-5xl mx-4 sm:mx-8 flex flex-col items-center gap-6">
            {/* Image */}
            <div className="relative w-full flex items-center justify-center" style={{ maxHeight: "70vh" }}>
              <img
                src={getFileUrl(lightbox.storage_path)}
                alt={cleanTitle(lightbox)}
                className="max-w-full max-h-[70vh] object-contain"
              />
              {/* Subtle glow */}
              <div className="absolute -inset-8 bg-[#D4AF37]/[0.02] blur-3xl pointer-events-none" />
            </div>

            {/* Info + Action */}
            <div className="text-center w-full max-w-md">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/60 mb-2">
                {getCategoryLabel(lightbox.image_type)}
              </p>
              <h3 data-testid="gallery-lightbox-title" className="font-heading text-2xl sm:text-3xl text-white mb-4">
                {cleanTitle(lightbox)}
              </h3>
              <div className="h-[1px] w-12 mx-auto bg-[#D4AF37]/30 mb-5" />
              <Button
                data-testid="gallery-lightbox-action"
                onClick={() => {
                  closeLightbox();
                  navigate(getDestination(lightbox).path);
                }}
                className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-xs h-auto transition-all duration-300"
              >
                {getDestination(lightbox).label}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
